import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import { axiosInstance } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Calendar, DollarSign, Users, Video, Settings, Wallet, BarChart3, XCircle, Globe, Trash2, Sparkles, Lock, Pencil } from "lucide-react";
import { toast } from "sonner";
import CreatorOnboarding from "@/components/CreatorOnboarding";
import { formatEventLocal } from "@/lib/eventTime";
import { isVisibleEventCategory } from "@/config/eventCategories";
import { canCreatorDeleteEvent } from "@/utils/eventDeletion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const IS_STAGING = process.env.REACT_APP_ENVIRONMENT === "staging";

const CreatorDashboard = ({ user, onLogout }) => {
  const [events, setEvents] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [unlockingProMode, setUnlockingProMode] = useState(null); // Track which event is being unlocked
  const [promoCode, setPromoCode] = useState("");
  const [showPromoInput, setShowPromoInput] = useState(null);
  const [eventPendingDeletion, setEventPendingDeletion] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    checkOnboarding();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, earningsRes] = await Promise.all([
        axiosInstance.get("/events/creator/my-events"),
        axiosInstance.get("/creator/earnings")
      ]);
      setEvents(eventsRes.data);
      setEarnings(earningsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const checkOnboarding = async () => {
    try {
      const response = await axiosInstance.get("/creator/onboarding-status");
      setOnboardingStatus(response.data);
      // Show onboarding if not complete
      if (!response.data.steps?.onboarding_completed) {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error("Error checking onboarding:", error);
    }
  };

  const handleCancelEvent = async (eventId, eventTitle) => {
    const confirmed = window.confirm(
      `Are you sure you want to cancel "${eventTitle}"?\n\nThis will automatically refund all ticket holders and cannot be undone.`
    );
    if (!confirmed) return;

    const reason = prompt("Enter cancellation reason (optional):", "Event cancelled by creator");
    if (reason === null) return; // User clicked cancel

    try {
      const response = await axiosInstance.post(`/events/${eventId}/cancel?reason=${encodeURIComponent(reason || "Event cancelled")}`);
      toast.success(`Event cancelled. ${response.data.refunded_count} tickets refunded.`);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel event");
    }
  };

  const handleDeleteEvent = (event) => {
    if (!canCreatorDeleteEvent(event)) {
      toast.error("Events that earned money cannot be deleted until the creator payout is completed.");
      return;
    }

    setEventPendingDeletion({
      id: event.id,
      title: event.title,
    });
  };

  const confirmDeleteEvent = async () => {
    if (!eventPendingDeletion) return;
    const eventId = eventPendingDeletion.id;
    setEventPendingDeletion(null);

    try {
      await axiosInstance.delete(`/events/creator/${eventId}`);
      toast.success("Event deleted successfully");
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete event");
    }
  };

  const handleUnlockProMode = async (eventId) => {
    setUnlockingProMode(eventId);
    try {
      const response = await axiosInstance.post("/pro-mode/unlock/checkout", {
        event_id: eventId,
        origin_url: window.location.origin,
        promo_code: promoCode || undefined
      });
      
      if (response.data.success && !response.data.url) {
        // 100% discount applied
        toast.success(response.data.message);
        setPromoCode("");
        setShowPromoInput(null);
        fetchData();
      } else if (response.data.url) {
        // Redirect to Stripe checkout
        const discountMsg = response.data.discount > 0 
          ? ` (${response.data.discount_description})` 
          : "";
        toast.info(`Redirecting to payment${discountMsg}...`);
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start Pro Mode unlock");
    } finally {
      setUnlockingProMode(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f]" data-testid="creator-dashboard">
      <Navbar user={user} onLogout={onLogout} isCreator={true} />
      
      {/* Onboarding Modal */}
      {showOnboarding && (
        <CreatorOnboarding 
          user={user}
          onClose={() => setShowOnboarding(false)}
          onComplete={() => {
            setShowOnboarding(false);
            checkOnboarding();
          }}
        />
      )}

      <AlertDialog
        open={Boolean(eventPendingDeletion)}
        onOpenChange={(open) => {
          if (!open) setEventPendingDeletion(null);
        }}
      >
        <AlertDialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl border-red-500/60 bg-[#171717] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center text-xl font-black leading-snug text-red-400">
              Delete this event?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-center text-base text-gray-300">
              <span className="block">This action cannot be undone. Would you like to proceed?</span>
              {eventPendingDeletion?.title && (
                <span className="block text-sm text-gray-400">
                  Event: “{eventPendingDeletion.title}”
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-2 grid grid-cols-2 gap-3 sm:grid sm:grid-cols-2 sm:space-x-0">
            <AlertDialogCancel className="mt-0 min-h-11 border-gray-600 bg-gray-800 font-bold text-white hover:bg-gray-700 hover:text-white">
              CANCEL
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteEvent}
              className="min-h-11 bg-red-600 font-bold text-white hover:bg-red-700"
            >
              DELETE
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <div className="pt-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-20">
        <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-4xl font-black leading-tight text-white md:text-5xl" data-testid="dashboard-title">Creator Dashboard</h1>
          <div className="w-full sm:w-auto">
            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:justify-end sm:gap-3">
            <Button
              onClick={() => navigate("/creator/analytics")}
              className="flex min-h-12 w-full min-w-0 items-center justify-center gap-2 bg-purple-600 px-3 py-3 font-bold text-white hover:bg-purple-700 sm:w-auto sm:px-4"
              data-testid="analytics-button"
            >
              <BarChart3 className="w-5 h-5" />
              Analytics
            </Button>
            <Button
              onClick={() => navigate("/creator/settings")}
              className="flex min-h-12 w-full min-w-0 items-center justify-center gap-2 bg-gray-700 px-3 py-3 font-bold text-white hover:bg-gray-600 sm:w-auto sm:px-4"
              data-testid="settings-button"
            >
              <Wallet className="w-5 h-5" />
              Payouts
            </Button>
            <Button
              onClick={() => navigate("/creator/create-event")}
              className="col-span-2 flex min-h-12 w-full min-w-0 items-center justify-center gap-2 bg-blue-600 px-4 py-3 font-bold text-white hover:bg-blue-700 sm:w-auto sm:px-6"
              data-testid="create-event-button"
            >
              <Plus className="w-5 h-5" />
              Create Event
            </Button>
            </div>
            <p className="mt-3 text-center text-sm font-bold text-blue-400 sm:text-right" data-testid="automatic-payout-notice">
              Automatic payouts within 24 hours after the event ends.
            </p>
          </div>
        </div>

        {/* Onboarding Progress Banner */}
        {onboardingStatus && !onboardingStatus.steps?.onboarding_completed && (
          <button
            onClick={() => setShowOnboarding(true)}
            className="w-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-lg p-4 mb-8 flex items-center justify-between hover:from-blue-600/30 hover:to-purple-600/30 transition-all"
            data-testid="onboarding-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">{onboardingStatus.current_step}/{onboardingStatus.total_steps}</span>
              </div>
              <div className="text-left">
                <p className="text-white font-semibold">Complete your setup</p>
                <p className="text-gray-400 text-sm">Finish setting up your creator account to start earning</p>
              </div>
            </div>
            <span className="text-blue-400 font-medium">Continue →</span>
          </button>
        )}

        {/* Earnings Overview */}
        <div className="grid md:grid-cols-3 xl:grid-cols-5 gap-4 mb-12">
          <div className="bg-gray-900/50 rounded-lg p-6" data-testid="total-revenue-card">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              <span className="text-gray-400 text-sm">Total Revenue</span>
            </div>
            <p className="text-white text-3xl font-bold">${earnings?.total_revenue?.toFixed(2) || "0.00"}</p>
          </div>

          <div className="bg-gray-900/50 rounded-lg p-6" data-testid="creator-earnings-card">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-blue-500" />
              <span className="text-gray-400 text-sm">Your Earnings (80%—no processing fees deducted)</span>
            </div>
            <p className="text-white text-3xl font-bold">${earnings?.creator_earnings?.toFixed(2) || "0.00"}</p>
          </div>

          <div className="bg-gray-900/50 rounded-lg p-6" data-testid="pending-payout-card">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-yellow-500" />
              <span className="text-gray-400 text-sm">Pending Payout</span>
            </div>
            <p className="text-white text-3xl font-bold">${earnings?.pending_payout?.toFixed(2) || "0.00"}</p>
          </div>

          <div className="bg-gray-900/50 rounded-lg p-6" data-testid="paid-out-card">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              <span className="text-gray-400 text-sm">Paid Out</span>
            </div>
            <p className="text-white text-3xl font-bold">${earnings?.paid_out?.toFixed(2) || "0.00"}</p>
          </div>

          <div className="bg-gray-900/50 rounded-lg p-6" data-testid="total-events-card">
            <div className="flex items-center gap-3 mb-2">
              <Video className="w-5 h-5 text-purple-500" />
              <span className="text-gray-400 text-sm">Total Events</span>
            </div>
            <p className="text-white text-3xl font-bold">{events.length}</p>
          </div>
        </div>

        {/* Events List */}
        <div>
          <h2 className="text-white text-2xl font-bold mb-6">Your Events</h2>
          
          {events.length === 0 ? (
            <div className="text-center py-20" data-testid="no-events-message">
              <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-xl mb-4">You haven't created any events yet</p>
              <Button
                onClick={() => navigate("/creator/create-event")}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
              >
                Create Your First Event
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
              {events.map((event) => (
                <div
                  key={event.id}
                  className={`bg-gray-900/50 rounded-lg overflow-hidden transition-all ${
                    event.status === "cancelled" ? "opacity-60" : "hover:bg-gray-900/70"
                  }`}
                  data-testid={`event-card-${event.id}`}
                >
                  <div className="aspect-[3/4] w-full relative">
                    <img
                      src={event.image_url}
                      alt={event.title}
                      className="w-full h-full object-cover"
                    />
                    <div className={`absolute top-2 right-2 px-3 py-1 rounded-full text-sm font-bold ${
                      event.status === "live" ? "bg-red-600 text-white animate-pulse" :
                      event.status === "cancelled" ? "bg-gray-600 text-white" :
                      event.status === "completed" ? "bg-green-600 text-white" :
                      "bg-blue-600 text-white"
                    }`}>
                      {event.status === "live" && "🔴 "}{event.status}
                    </div>
                    {event.geo_restricted && (
                      <div className="absolute top-2 left-2 bg-purple-600 text-white px-2 py-1 rounded-full text-xs">
                        <Globe className="w-3 h-3 inline mr-1" />
                        Geo-Restricted
                      </div>
                    )}
                    {event.status === "cancelled" && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold">
                          CANCELLED
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-white text-xl font-bold mb-2">{event.title}</h3>
                    <div className="flex items-center gap-2 text-gray-300 mb-2">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">{formatEventLocal(event)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300 mb-4">
                      <DollarSign className="w-4 h-4" />
                      <span className="text-sm">Revenue: ${event.total_revenue?.toFixed(2) || "0.00"}</span>
                    </div>

                    {isVisibleEventCategory(event.category) && (
                      <Button
                        onClick={() => navigate(`/creator/religious-report/${event.id}`)}
                        className="w-full mb-3 bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 border border-purple-500/40 text-sm flex items-center justify-center gap-2"
                        data-testid={`religious-report-button-${event.id}`}
                      >
                        <BarChart3 className="w-4 h-4" />
                        {event.category} Event Report
                      </Button>
                    )}
                    
                    {event.status !== "cancelled" && event.status !== "completed" ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          {event.streaming_package === "premium" ? (
                            <Button
                              onClick={() => navigate(`/pro-mode/${event.id}`)}
                              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm"
                              data-testid={`pro-mode-button-${event.id}`}
                            >
                              <Sparkles className="w-4 h-4 mr-1" />
                              Pro Mode
                            </Button>
                          ) : (
                            <>
                              <Button
                                onClick={() => navigate(`/control-panel/${event.id}`)}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm"
                                data-testid={`manage-button-${event.id}`}
                              >
                                <Video className="w-4 h-4 mr-1" />
                                Stream
                              </Button>
                              <Button
                                onClick={() => setShowPromoInput(showPromoInput === event.id ? null : event.id)}
                                className="flex-1 bg-gradient-to-r from-purple-600/80 to-pink-600/80 hover:from-purple-600 hover:to-pink-600 text-white text-sm"
                                data-testid={`unlock-pro-mode-button-${event.id}`}
                              >
                                <Lock className="w-4 h-4 mr-1" />
                                {IS_STAGING ? "Unlock Pro Mode Preview" : "Pro Mode $1000"}
                              </Button>
                            </>
                          )}
                        </div>

                        {event.status === "upcoming" && (
                          <Button
                            onClick={() => navigate(`/creator/edit-event/${event.id}`)}
                            className="w-full bg-blue-600/20 hover:bg-blue-600/40 text-blue-300 border border-blue-500/50 text-sm"
                            data-testid={`edit-button-${event.id}`}
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Edit Event
                          </Button>
                        )}
                        
                        {/* Pro Mode Unlock Panel */}
                        {showPromoInput === event.id && event.streaming_package !== "premium" && (
                          <div className="bg-gray-800/50 rounded-lg p-3 border border-purple-500/30">
                            <p className="text-gray-300 text-xs mb-2">
                              {IS_STAGING
                                ? "Unlock Pro Mode free in staging to test multi-camera controls"
                                : "Unlock multi-camera streaming with Pro Mode"}
                            </p>
                            {!IS_STAGING && <div className="flex gap-2 mb-2">
                              <input
                                type="text"
                                value={promoCode}
                                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                placeholder="Promo code (optional)"
                                className="flex-1 bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-white text-sm placeholder-gray-500"
                              />
                            </div>}
                            <Button
                              onClick={() => handleUnlockProMode(event.id)}
                              disabled={unlockingProMode === event.id}
                              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm"
                            >
                              {unlockingProMode === event.id ? "Processing..." : "Unlock Pro Mode"}
                            </Button>
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => {
                              navigator.clipboard.writeText(event.share_link);
                              toast.success("Link copied!");
                            }}
                            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-sm"
                            data-testid={`share-button-${event.id}`}
                          >
                            Share
                          </Button>
                          <Button
                            onClick={() => handleCancelEvent(event.id, event.title)}
                            className="flex-1 bg-orange-600/20 hover:bg-orange-600/40 text-orange-400 border border-orange-600/50 text-sm"
                            data-testid={`cancel-button-${event.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        </div>
                        {canCreatorDeleteEvent(event) && (
                          <Button
                            onClick={() => handleDeleteEvent(event)}
                            className="w-full bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/50 text-sm"
                            data-testid={`delete-button-${event.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete Event
                          </Button>
                        )}
                      </div>
                    ) : event.status === "completed" ? (
                      <div className="space-y-2">
                        <div className="text-center text-gray-400 py-2">
                          Event completed
                        </div>
                        {canCreatorDeleteEvent(event) && (
                          <Button
                            onClick={() => handleDeleteEvent(event)}
                            className="w-full bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/50 text-sm"
                            data-testid={`delete-button-${event.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Event
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-center text-red-400 py-2 text-sm">
                          {event.cancellation_reason || "Event was cancelled"}
                        </div>
                        {canCreatorDeleteEvent(event) && (
                          <Button
                            onClick={() => handleDeleteEvent(event)}
                            className="w-full bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/50 text-sm"
                            data-testid={`delete-button-${event.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Event
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorDashboard;
